import { useEffect, useRef } from 'react'
import { useFormState } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { keyBy } from 'lodash-es'
import { useDebouncedCallback } from 'use-debounce'
import { useResultsStore } from '@editor/stores'
import { useMutation } from '@tanstack/react-query'
import { enqueueSnackbar } from '@ui/snackbar'
import { saveManualLoadsPerSupport } from '@mutations'
import type { Results2ListItem } from '../types'

type AutosavePayload = {
  items: Results2ListItem[]
  token: number
}

type UseAnchorCommentAutosaveProps = {
  /** Live form items — every change while the form is dirty re-arms the debounced save. */
  items: Results2ListItem[]
  /** Only the anchors list saves its comments through the manual-loads endpoint. */
  enabled: boolean
  disableForm: boolean
}

type UseAnchorCommentAutosaveResult = {
  isSaving: boolean
  dirty: boolean
  /** Run a pending debounced save immediately (save icon, unmount). */
  flushAutosave: () => void
}

/**
 * Debounced autosave of the anchor list's comment column. Anchor comments are persisted with the
 * manual loads (`manual-loads-per-support`), so the payload replays every support's saved manual
 * loads and only swaps in the edited comments. The saved comments are mirrored back into the
 * results store so the detail pane and export see them.
 */
export const useAnchorCommentAutosave = ({
  items,
  enabled,
  disableForm,
}: UseAnchorCommentAutosaveProps): UseAnchorCommentAutosaveResult => {
  const { projectId } = useParams<{ projectId: string }>()

  const setAnchorInterventionTableData = useResultsStore(
    state => state.setAnchorInterventionTableData,
  )
  const dirty = useResultsStore(state => state.elementListGroupingDirty)
  const setElementListGroupingDirty = useResultsStore(state => state.setElementListGroupingDirty)

  // Only the newest armed save may mirror its result / clear the dirty flag (no abort support in
  // the manual-loads request, so superseded responses are ignored instead of cancelled).
  const latestTokenRef = useRef(0)

  const { mutate: mutateAutosave, isLoading: isSaving } = useMutation({
    mutationKey: saveManualLoadsPerSupport.getKey(projectId as string),
    mutationFn: async ({ items: nextItems }: AutosavePayload) => {
      // The endpoint replaces every support's manual loads, so read them from the store AT FIRE
      // TIME (after the debounce, possibly after unmount) — a render closure could lag behind a
      // refetch and resurrect outdated manual loads.
      const anchorRows = useResultsStore.getState().anchorInterventionTableData
      if (!projectId || !anchorRows) return undefined

      const itemById = keyBy(nextItems, 'id')
      const requestData: ManualLoadOnSupport[] = anchorRows.map(row => ({
        support_id: row.support_guid,
        manual_loads: row.manual_loads,
        comment: itemById[row.support_guid]?.comment ?? row.comment,
        connectorSelectionPolicy: row.connectorSelectionPolicy,
        userDefinedConnectorId: row.userDefinedConnectorId,
      }))

      await saveManualLoadsPerSupport.request(projectId, requestData)
      return requestData
    },
    onSuccess: (data: ManualLoadOnSupport[] | undefined, variables: AutosavePayload) => {
      const anchorRows = useResultsStore.getState().anchorInterventionTableData
      if (latestTokenRef.current !== variables.token || !data || !anchorRows) return

      const savedBySupport = keyBy(data, 'support_id')
      // Mirror the saved comments only — manual loads stay whatever the store holds.
      setAnchorInterventionTableData(
        anchorRows.map(row => ({
          ...row,
          comment: savedBySupport[row.support_guid]?.comment ?? row.comment,
        })),
      )
    },
    onError: (error: unknown) => {
      enqueueSnackbar(
        buildErrorMessage(error as AxiosError, 'Fehler beim Speichern der Kommentare'),
        {
          variant: 'error',
        },
      )
    },
    onSettled: (_data, _error, variables: AutosavePayload) => {
      if (latestTokenRef.current === variables.token) {
        setElementListGroupingDirty(false)
      }
    },
  })

  const debouncedAutosave = useDebouncedCallback(mutateAutosave, 1000)

  const { dirtyFields } = useFormState()

  useEffect(() => {
    if (!enabled || disableForm || !('items' in dirtyFields)) return

    latestTokenRef.current += 1
    setElementListGroupingDirty(true)
    debouncedAutosave({ items, token: latestTokenRef.current })
  }, [enabled, disableForm, setElementListGroupingDirty, items, dirtyFields, debouncedAutosave])

  // Flush a pending save when the list unmounts (e.g. tab switch).
  useEffect(() => {
    if (!enabled) return
    return () => {
      debouncedAutosave.flush()
      // The unmounting list can no longer own the "dirty" disable. A just-flushed save keeps
      // the anchor detail form disabled via the `saveManualLoadsPerSupport` mutation counter in
      // Results2's `disableDetailForm`, so manual-load edits can't race the in-flight request.
      setElementListGroupingDirty(false)
    }
  }, [enabled, debouncedAutosave, setElementListGroupingDirty])

  return { isSaving, dirty, flushAutosave: debouncedAutosave.flush }
}
